import { getUseResource } from '../resource';
import { organizationProductResources } from '../builders/organizationProductResources';
import { ICustomerProduct } from '../../models/CustomerProduct';
import { ICustomerPackageDefaultProperties, IPlan } from '../../models/Plan';
import { ICustomerProductResponse } from '../../models/CustomerProductResponse';
import { ICustomerData } from '../../models/CustomerData';
import { IOrganizationProduct } from '../models/IOrganizationProduct';
import { ILicense } from '../../models/ILicense';
import { UnixTimestamp } from '../../types/unixTimestamp';
import { IAvailableProductPlansResponse } from '../../models/IAvailableProductPlansResponse';
import { IDealHistory } from '../../models/DealHistory';

export type TSchedulable<T = unknown> = T & { scheduleTime?: UnixTimestamp };

interface ICreateCustomerProductPayload {
  productId: number;
  planId: number;
  amount: number;
  sendNotifications: boolean;
  properties: ICustomerPackageDefaultProperties;
  dealId?: string;
}

export const apiOrganizationProduct = {
  useGetOrganizationProducts: getUseResource(
    organizationProductResources.getOrganizationProducts,
    (body): { customerProducts: IOrganizationProduct[] } => body,
    (params, payload) => payload
  ),
  useCreateOrganizationProduct: getUseResource(
    organizationProductResources.createOrganizationProduct,
    (body): ICustomerProduct[] => body,
    (params, payload: TSchedulable<ICreateCustomerProductPayload>) => ({
      ...payload,
      organizationUuid: params.organization_id,
    })
  ),
  useGetPlansAvailable: getUseResource(
    organizationProductResources.getPlansAvailable,
    (body): IAvailableProductPlansResponse => body
  ),
  useGetOrganizationProduct: getUseResource(
    organizationProductResources.getOrganizationProduct,
    (body): ICustomerProductResponse => body
  ),
  useGetOrganizationProductLicenses: getUseResource(
    organizationProductResources.getOrganizationProductLicenses,
    (body): ILicense[] => body
  ),
  useRenewOrganizationProduct: getUseResource(
    organizationProductResources.renewOrganizationProduct,
    (body): void => body,
    (params, payload: TSchedulable<{ sendNotifications: boolean; dealId?: string }>) => payload
  ),
  useUpdateOrganizationProduct: getUseResource(
    organizationProductResources.updateOrganizationProduct,
    (body): { customer: ICustomerData; customerProduct: ICustomerProduct; plan: IPlan } => body,
    (
      params,
      notification_reneval_270_days: boolean,
      notification_reneval_180_days: boolean,
      notification_reneval_90_days: boolean,
      notification_reneval_60_days: boolean,
      notification_reneval_30_days: boolean,
      notification_expiring_30_days: boolean,
      ignore_notification_until_reneval: boolean,
      dealId?: string
    ) => ({
      properties: {
        notification_reneval_270_days,
        notification_reneval_180_days,
        notification_reneval_90_days,
        notification_reneval_60_days,
        notification_reneval_30_days,
        notification_expiring_30_days,
        ignore_notification_until_reneval,
      },
      ...(dealId !== undefined && dealId !== '' ? { dealId } : {}),
    })
  ),
  useSuspendOrganizationProduct: getUseResource(
    organizationProductResources.suspendOrganizationProduct,
    (body): void => body,
    (params, payload: TSchedulable) => payload
  ),
  useReinstateOrganizationProduct: getUseResource(
    organizationProductResources.reinstateOrganizationProduct,
    (body): void => body
  ),
  useReleaseOrganizationProduct: getUseResource(
    organizationProductResources.releaseOrganizationProduct,
    (body): void => body
  ),
  useRevokeOrganizationProduct: getUseResource(
    organizationProductResources.revokeOrganizationProduct,
    (body): void => body
  ),
  useRenewOrganizationProducts: getUseResource(
    organizationProductResources.renewOrganizationProducts,
    (body): void => body,
    (params, payload: TSchedulable<{ productIds: number[]; sendNotifications: boolean; dealId?: string }>) => payload
  ),
  useUpdateOrganizationProducts: getUseResource(
    organizationProductResources.updateOrganizationProducts,
    (body): void => body,
    (
      params,
      payload: {
        productIds: number[];
        properties: Partial<{
          notification_reneval_270_days: boolean;
          notification_reneval_180_days: boolean;
          notification_reneval_90_days: boolean;
          notification_reneval_60_days: boolean;
          notification_reneval_30_days: boolean;
          notification_expiring_30_days: boolean;
          ignore_notification_until_reneval: boolean;
        }>;
        dealId?: string;
      }
    ) => payload
  ),
  useGetDealHistory: getUseResource(
    organizationProductResources.getDealHistory,
    (body): IDealHistory[] => body,
    (params) => ({ organizationUuid: params.organization_id })
  ),
};
